import { useEffect, useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { Typography } from '@remarkable/ark-web';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { CheckoutStepCompleted } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import {
  CHECKOUT_STEPS,
  ComponentLocations,
} from 'src/analytics/trackingTypes';
import amex from 'src/assets/icons/amex.svg';
import mastercard from 'src/assets/icons/mastercard.svg';
import paypal from 'src/assets/icons/paypal.svg';
import visa from 'src/assets/icons/visa.svg';
import {
  Button,
  Divider,
  ErrorBoundary,
  Feedback,
  NotificationBox,
  SomethingWentWrong,
  Spinner,
} from 'src/components';
import { AppPage } from 'src/components/AppPage';

import { Address } from './Address';
import { CheckoutDetailsForm } from './CheckoutDetailsForm';
import { PaymentForm } from './PaymentForm';
import { Summary } from './Summary';
import { useCheckout } from './useCheckout';

// CheckoutV2 is the main page for the Stripe Sessions and cloud-connect backed checkout.
export const CheckoutV2 = () => {
  const { t } = useLingui();
  const checkout = useCheckout();
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const [redirectError, setRedirectError] = useState(false);

  useEffect(() => {
    tracker.trackEvent(
      new CheckoutStepCompleted({
        checkout_step: CHECKOUT_STEPS.CHECKOUT_V2_STARTED,
      })
    );
  }, []);

  // A redirect-based payment method (e.g. PayPal) that failed sends the
  // customer back to this page. Show the error and clear the param so it
  // doesn't persist on reload.
  useEffect(() => {
    if (search.redirect_status === 'failed') {
      setRedirectError(true);
      void navigate({ to: '.', replace: true, search: undefined });
    }
  }, []);

  // `checkout.error` also covers the price/trial-offer queries (not just the
  // checkout-session mutation), so those failures aren't silently swallowed.
  // Not gating on `checkout.isLoading` here: price/trial-offer aren't needed
  // to render or submit the details step itself (only the page title and,
  // in a later PR, the payment step's order summary), so blocking the whole
  // page on them would delay the details form for no functional reason.
  if (checkout.checkoutSession.isError || checkout.error) {
    return (
      <SomethingWentWrong
        error={checkout.checkoutSession.error ?? checkout.error ?? null}
      />
    );
  }

  return (
    <AppPage.Container>
      <ErrorBoundary>
        <div className="m-auto my-32 max-w-lm px-16 text-center ll:mt-64 ll:mb-80">
          <Typography as="h1" className="mb-24" variant="responsive-heading-lg">
            {checkout.trialOffer ? (
              <Trans>Activate Connect</Trans>
            ) : (
              <Trans>Subscribe to Connect</Trans>
            )}
          </Typography>

          <Typography variant="responsive-body-md" className="my-0">
            <Trans>
              You&apos;re one step away from starting your subscription.
            </Trans>
          </Typography>
        </div>

        <AppPage.Content className="max-w-l2xl">
          {redirectError && (
            <NotificationBox
              className="mb-24"
              variant="error"
              title={t`Payment failed`}
              data-cy="redirect-error"
            >
              <Trans>Please try again with a different payment method.</Trans>
            </NotificationBox>
          )}
          <div className="flex flex-wrap-reverse gap-32 ll:gap-48">
            <div className="order-2 flex flex-[1_1_400px] flex-col gap-24">
              <Typography
                as="h2"
                className="hidden ll:block"
                variant="heading-sm"
                fontWeight="regular"
              >
                <Trans>Order summary</Trans>
              </Typography>
              <Summary
                trialOffer={checkout.trialOffer}
                price={checkout.price}
                checkoutPrice={checkout.checkoutSession.data?.price}
                isLoading={checkout.isLoadingOffer}
                isLoadingPrice={checkout.isLoadingPrice}
                promotion={checkout.promotion.appliedPromotion}
                showTaxBreakdown={
                  checkout.checkoutFormState.currentStep === 'PAYMENT'
                }
              />
            </div>

            <div className="order-1 flex flex-[1_1_400px] flex-col gap-40">
              <div className="flex flex-col gap-24">
                <div className="flex items-center justify-start gap-24">
                  <Typography
                    as="h2"
                    variant="responsive-heading-sm"
                    fontWeight="regular"
                  >
                    <Trans>1. Contact information</Trans>
                  </Typography>

                  {checkout.paymentStep && (
                    <Button
                      variant="tertiary-neutral"
                      size="small"
                      onClick={() => {
                        checkout.setCheckoutFormState({
                          ...checkout.checkoutFormState,
                          currentStep: 'DETAILS',
                        });
                      }}
                      analytics={{
                        text: t`Edit`,
                        location: ComponentLocations.CHECKOUT.EDIT,
                        action: 'edit contact details',
                      }}
                    >
                      <Trans>Edit</Trans>
                    </Button>
                  )}
                </div>
                {checkout.paymentStep ? (
                  <Address />
                ) : (
                  <CheckoutDetailsForm
                    startCheckoutSession={checkout.startCheckoutSession}
                    isLoadingSession={checkout.isLoadingSession}
                    promotion={checkout.promotion}
                  />
                )}
              </div>

              <Divider />

              <div className="flex flex-col gap-16">
                <div className="flex flex-row justify-between gap-6">
                  <Typography
                    as="h2"
                    textWrap="nowrap"
                    variant="responsive-heading-sm"
                    fontWeight="regular"
                  >
                    <Trans>2. Payment</Trans>
                  </Typography>
                  <div className="flex justify-start gap-8">
                    <img width={42} src={visa} alt="Visa"></img>
                    <img width={42} src={mastercard} alt="Mastercard"></img>
                    <img width={42} src={amex} alt="American Express"></img>
                    <img width={42} src={paypal} alt="PayPal"></img>
                  </div>
                </div>
                {checkout.checkoutFormState.currentStep === 'PAYMENT' && (
                  <>
                    {checkout.isLoadingSession && <Spinner />}
                    {checkout.checkoutSession.data && (
                      <PaymentForm
                        checkoutSession={checkout.checkoutSession.data}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <Feedback
            className="mt-320 mb-32"
            title={t`Did you find what you were looking for?`}
            feedbackTargetId="store-connect-checkout"
          />
        </AppPage.Content>
      </ErrorBoundary>
    </AppPage.Container>
  );
};
